import { FC, useEffect } from 'react';
import uniqBy from 'lodash/uniqBy';
import useBenguide from 'hooks/benguide';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import AddPlanApiRequestPayload from 'model/AddPlanApiRequestPayload';
import Plan from 'model/Plan';
import { buildPlanRefsFromPlan } from 'modules/benefitsWellness/util';
import { TaxAdvantagedSection } from 'modules/taxAdvantaged/constants/taxAdvantagedConstants';
import {
  fetchFSAPlans,
  getDefaultMediaForFSA,
  getFSASection,
  getPlansFromPlanRefs,
  selectFSAPlans,
} from 'modules/taxAdvantaged/slices/fsaSlice';
import TaxAdvantagedPlanOverview from 'modules/taxAdvantaged/components/TaxAdvantagePlanOverview/TaxAdvantagedPlanOverview';

const FSAPlans: FC = () => {
  const dispatch = useAppDispatch();

  const { isEditMode, latestRevision } = useBenguide();
  const benguideData = useAppSelector((state) => state.benguide.data);
  const planRefs = useAppSelector((state) => state.fsa.fsaSection.plans);
  const planList = useAppSelector((state) => state.fsa.planList);
  const { data: selectedPlans, inProgress } = useAppSelector(
    (state) => state.fsa.selectedPlans
  );

  useEffect(() => {
    const { employerId, planYearId, benefitClasses } = benguideData || {};
    if (employerId && planYearId && isEditMode) {
      dispatch(
        fetchFSAPlans({
          employerId,
          planYearId,
          benefitClasses,
        })
      );
    }
  }, [dispatch, benguideData, isEditMode]);

  useEffect(() => {
    dispatch(getPlansFromPlanRefs({ planRefs }));
  }, [dispatch, planRefs]);

  useEffect(() => {
    dispatch(
      getFSASection({
        benguideId: benguideData.masterId,
      })
    );
  }, [dispatch, benguideData.masterId, latestRevision]);

  useEffect(() => {
    if (selectedPlans) {
      const uniquePlansList = uniqBy(selectedPlans, 'benefitKind');
      if (uniquePlansList) {
        uniquePlansList.forEach((element: any) => {
          if (element.id && element.benefitKind) {
            dispatch(
              getDefaultMediaForFSA(
                benguideData.masterId,
                element.id,
                element.benefitKind
              )
            );
          }
        });
      }
    }
  }, [dispatch, selectedPlans, benguideData.masterId]);

  const handleSave = (planList: Plan[], orderByPlanNames: boolean) => {
    const planRefs = planList.map((plan) =>
      buildPlanRefsFromPlan(plan, plan.benefitKind)
    );
    const payload: AddPlanApiRequestPayload = {
      orderByPlanNames: orderByPlanNames,
      plans: planRefs,
      sectionName: TaxAdvantagedSection.FSA,
    };

    dispatch(
      selectFSAPlans({
        addPlansPayload: payload,
        benefitKind: TaxAdvantagedSection.FSA,
        benguideId: benguideData.masterId,
      })
    );
  };

  return (
    <TaxAdvantagedPlanOverview
      onSelectPlans={handleSave}
      planList={planList.data}
      planListLoading={planList.inProgress}
      planRefs={planRefs}
      section={TaxAdvantagedSection.FSA}
      selectedPlans={selectedPlans}
      selectedPlansLoading={inProgress}
    />
  );
};

export default FSAPlans;
